import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Divider,
  Chip,
  Avatar,
  Snackbar,
  Alert,
  IconButton,
  Paper,
} from '@mui/material';
import { motion } from 'framer-motion';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CategoryIcon from '@mui/icons-material/Category';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { getBlogPost, addComment } from '../../../services/api';

const BlogPostDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comment, setComment] = useState({ name: '', email: '', content: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchBlogPost();
  }, [slug]);

  const fetchBlogPost = async () => {
    try {
      const response = await getBlogPost(slug);
      setPost(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch blog post');
      setLoading(false);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    try {
      await addComment(slug, comment);
      setSnackbar({
        open: true,
        message: 'Comment submitted successfully!',
        severity: 'success',
      });
      setComment({ name: '', email: '', content: '' });
      fetchBlogPost(); // Refresh comments
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to submit comment. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ pt: { xs: '120px', md: '140px' }, textAlign: 'center' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ pt: { xs: '120px', md: '140px' }, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: { xs: '120px', md: '140px' } }}>
      {/* Blog Post Header with Image */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '300px', md: '400px' },
          mb: 6,
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={post.image}
          alt={post.title}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.7))',
            display: 'flex',
            alignItems: 'flex-end',
            p: 4,
          }}
        >
          <Container>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h2"
                color="white"
                sx={{
                  fontSize: { xs: '2rem', md: '3rem' },
                  fontWeight: 700,
                  mb: 2,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                }}
              >
                {post.title}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Chip
                  icon={<CategoryIcon />}
                  label={post.category}
                  sx={{ 
                    backgroundColor: 'rgba(0, 128, 128, 0.9)',
                    color: 'white',
                    '& .MuiChip-icon': {
                      color: 'white'
                    }
                  }}
                />
                <Chip
                  icon={<AccessTimeIcon />}
                  label={new Date(post.date).toLocaleDateString()}
                  sx={{ 
                    backgroundColor: 'rgba(0, 128, 128, 0.9)',
                    color: 'white',
                    '& .MuiChip-icon': {
                      color: 'white'
                    }
                  }}
                />
              </Box>
            </motion.div>
          </Container>
        </Box>
      </Box>

      {/* Main Content */}
      <Container maxWidth="md">
        <Grid container spacing={4}>
          {/* Content Column */}
          <Grid item xs={12} md={8}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {/* Metrics Section */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 4,
                  display: 'flex',
                  gap: 3,
                  backgroundColor: 'transparent',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VisibilityIcon color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    {post.views} views
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ThumbUpIcon color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    {post.likes_count} likes
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ThumbDownIcon color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    {post.dislikes_count} dislikes
                  </Typography>
                </Box>
              </Paper>

              {/* Blog Post Content */}
              <Box
                sx={{
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    mt: 4,
                    mb: 2,
                    color: 'text.primary',
                  },
                  '& p': {
                    mb: 2,
                    lineHeight: 1.8,
                    color: 'text.secondary',
                    fontSize: '1.1rem',
                  },
                  '& ol, & ul': {
                    mb: 2,
                    pl: 4,
                  },
                  '& li': {
                    mb: 1,
                    color: 'text.secondary',
                    fontSize: '1.1rem',
                  },
                  '& img': {
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: 2,
                    my: 3,
                    boxShadow: 2,
                  },
                  '& blockquote': {
                    borderLeft: '4px solid',
                    borderColor: 'primary.main',
                    pl: 2,
                    py: 1,
                    my: 3,
                    backgroundColor: 'action.hover',
                    borderRadius: '0 4px 4px 0',
                  },
                  '& a': {
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  },
                }}
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </motion.div>
          </Grid>

          {/* Sidebar Column */}
          <Grid item xs={12} md={4}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {/* Author Info */}
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      sx={{ width: 56, height: 56, mr: 2 }}
                      src={post.author?.profile_image}
                    >
                      {post.author?.username?.[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {post.author?.username || 'Anonymous'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Author
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {post.author?.bio || 'No bio available'}
                  </Typography>
                </CardContent>
              </Card>

              {/* Comments Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom>
                  Comments ({post.comments.length})
                </Typography>
                {post.comments.map((comment) => (
                  <Card key={comment.id} sx={{ mb: 2, backgroundColor: 'background.paper' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Avatar sx={{ mr: 2, width: 32, height: 32 }}>{comment.name[0]}</Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {comment.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(comment.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ pl: 7 }}>
                        {comment.content}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>

              {/* Comment Form */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Leave a Comment
                  </Typography>
                  <Box
                    component="form"
                    onSubmit={handleCommentSubmit}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label="Name"
                      value={comment.name}
                      onChange={(e) => setComment({ ...comment, name: e.target.value })}
                      required
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Email"
                      type="email"
                      value={comment.email}
                      onChange={(e) => setComment({ ...comment, email: e.target.value })}
                      required
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Comment"
                      multiline
                      rows={3}
                      value={comment.content}
                      onChange={(e) => setComment({ ...comment, content: e.target.value })}
                      required
                      sx={{ mb: 2 }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                    >
                      Post Comment
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BlogPostDetail; 